import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// NFC short-URL redirect shim. Chips encode https://<host>/n/{token}; the
// K8s ingress routes /n/* to the frontend (not backend), so we bounce the
// browser to /api/t/{token} where the FastAPI redirect handler lives.
function NfcShortRedirect() {
  const { token } = useParams();
  if (typeof window !== "undefined" && token) {
    window.location.replace(`/api/t/${encodeURIComponent(token)}`);
  }
  return null;
}
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PublicProfile from "@/pages/PublicProfile";
import SeoLanding from "@/pages/seo/SeoLanding";
import PricingPage from "@/pages/seo/PricingPage";
import { About, Contact, Security } from "@/pages/seo/CompanyPages";
import ComparePage from "@/pages/seo/ComparePage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Admin from "@/pages/Admin";
import Home from "@/pages/Home";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import IndustryStudio from "@/pages/IndustryStudio";
import Signatures from "@/pages/Signatures";
import IntegrationHub from "@/pages/IntegrationHub";
import Leads from "@/pages/Leads";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Landing from "@/pages/Landing";
import IndustryShowcase from "@/pages/IndustryShowcase";
import CreateCard from "@/pages/CreateCard";
import Billing from "@/pages/Billing";
import CommercialSettings from "@/pages/CommercialSettings";
import Referral from "@/pages/Referral";
import PartnerApply from "@/pages/partner/PartnerApply";
import PartnerApplyWizard from "@/pages/partner/PartnerApplyWizard";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PartnerCustomers from "@/pages/partner/PartnerCustomers";
import PartnerCommissions from "@/pages/partner/PartnerCommissions";
import PartnerCodes from "@/pages/partner/PartnerCodes";
import PartnerPayouts from "@/pages/partner/PartnerPayouts";
import PartnerMarketing from "@/pages/partner/PartnerMarketing";
import PartnerTerms from "@/pages/legal/PartnerTerms";
import PartnerProgramPublic from "@/pages/PartnerProgramPublic";
import Meetings from "@/pages/Meetings";
import AvailabilityAdmin from "@/pages/AvailabilityAdmin";
import ManageMeeting from "@/pages/ManageMeeting";
import AcceptInvite from "@/pages/AcceptInvite";
import Activate from "@/pages/Activate";
import NfcCards from "@/pages/NfcCards";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthExtra";import Legal from "@/pages/Legal";
import ControlCenter from "@/pages/ControlCenter";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import GoogleFinish from "@/pages/GoogleFinish";
import AppleFinish from "@/pages/AppleFinish";
import LocaleToast from "@/components/LocaleToast";
import ConsentBanner from "@/components/ConsentBanner";
import GAListener from "@/components/GAListener";
import PrivacyCenter from "@/pages/PrivacyCenter";
import Privacy from "@/pages/Privacy";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050607]">
        <Loader2 className="w-6 h-6 animate-spin text-[#D6A653]" />
      </div>
    );
  }
  if (!user) {
    const path = location.pathname + location.search;
    const target = location.pathname.startsWith("/partner/")
      ? `/login?intent=partner&return_to=${encodeURIComponent(path)}`
      : `/login?redirect=${encodeURIComponent(path)}`;
    return <Navigate to={target} replace />;
  }
  return children;
}

// SUPER_ADMIN-only routes (/control/*). Normal customers are redirected away.
function SuperAdminRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050607]">
        <Loader2 className="w-6 h-6 animate-spin text-[#D6A653]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "SUPER_ADMIN") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <GAListener />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/register" element={<Register />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/n/:token" element={<NfcShortRedirect />} />
            <Route path="/nfc/lost" element={
              <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center px-6" data-testid="nfc-lost-page">
                <div className="max-w-md text-center">
                  <p className="text-xs uppercase tracking-wider text-[#D6A653]">TapPresence · NFC</p>
                  <h1 className="mt-3 text-2xl font-medium">This tag has been reported lost.</h1>
                  <p className="mt-3 text-sm text-white/60">If you found it, please return it to the owner. It can no longer be used to open a digital business card.</p>
                </div>
              </div>
            } />
            <Route path="/nfc/inactive" element={
              <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center px-6" data-testid="nfc-inactive-page">
                <div className="max-w-md text-center">
                  <p className="text-xs uppercase tracking-wider text-[#D6A653]">TapPresence · NFC</p>
                  <h1 className="mt-3 text-2xl font-medium">This tag is not currently active.</h1>
                  <p className="mt-3 text-sm text-white/60">The owner has deactivated it. If this is your tag, sign in and reactivate it from your NFC settings.</p>
                </div>
              </div>
            } />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/auth/google/finish" element={<GoogleFinish />} />
            <Route path="/auth/apple/finish" element={<AppleFinish />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-center" element={<PrivacyCenter />} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/nfc" element={<ProtectedRoute><NfcCards /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
            <Route path="/admin/platform" element={<Navigate to="/control" replace />} />
            <Route path="/control" element={<SuperAdminRoute><ControlCenter /></SuperAdminRoute>} />
            <Route path="/control/:section" element={<SuperAdminRoute><ControlCenter /></SuperAdminRoute>} />
            <Route path="/industry-studio" element={<SuperAdminRoute><IndustryStudio /></SuperAdminRoute>} />
            <Route path="/signatures" element={<ProtectedRoute><Signatures /></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><IntegrationHub /></ProtectedRoute>} />
            <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
            <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
            <Route path="/templates" element={<ProtectedRoute><CreateCard /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/partner/apply" element={<Navigate to="/partners/apply" replace />} />
            <Route path="/partners/apply" element={<PartnerApplyWizard />} />
            {/* Old single-step apply page is retained under a legacy path in case
                any external email links still reference it explicitly. */}
            <Route path="/partner/apply-legacy" element={<ProtectedRoute><PartnerApply /></ProtectedRoute>} />
            <Route path="/partner/dashboard" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
            <Route path="/partner/customers" element={<ProtectedRoute><PartnerCustomers /></ProtectedRoute>} />
            <Route path="/partner/commissions" element={<ProtectedRoute><PartnerCommissions /></ProtectedRoute>} />
            <Route path="/partner/codes" element={<ProtectedRoute><PartnerCodes /></ProtectedRoute>} />
            <Route path="/partner/payouts" element={<ProtectedRoute><PartnerPayouts /></ProtectedRoute>} />
            <Route path="/partner/marketing" element={<ProtectedRoute><PartnerMarketing /></ProtectedRoute>} />
            <Route path="/legal/partner-terms" element={<PartnerTerms />} />
            <Route path="/referral" element={<ProtectedRoute><Referral /></ProtectedRoute>} />
            <Route path="/admin/commercial" element={<Navigate to="/control/plans" replace />} />
            <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
            <Route path="/availability" element={<ProtectedRoute><AvailabilityAdmin /></ProtectedRoute>} />
            <Route path="/m/:token" element={<ManageMeeting />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/industries" element={<IndustryShowcase />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/digital-business-card" element={<SeoLanding />} />
            <Route path="/nfc-business-card" element={<SeoLanding />} />
            <Route path="/business-card-scanner" element={<SeoLanding />} />
            <Route path="/lead-capture" element={<SeoLanding />} />
            <Route path="/teams" element={<SeoLanding />} />
            <Route path="/real-estate" element={<SeoLanding />} />
            <Route path="/sales-teams" element={<SeoLanding />} />
            <Route path="/event-networking" element={<SeoLanding />} />
            <Route path="/consultants" element={<SeoLanding />} />
            <Route path="/healthcare" element={<SeoLanding />} />
            <Route path="/digital-business-card-uae" element={<SeoLanding />} />
            <Route path="/digital-business-card-dubai" element={<SeoLanding />} />
            <Route path="/event-lead-capture" element={<SeoLanding />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/security" element={<Security />} />
            <Route path="/compare/:comp" element={<ComparePage />} />
            <Route path="/partners" element={<PartnerProgramPublic />} />
            <Route path="/:slug" element={<PublicProfile />} />
          </Routes>
          <ConsentBanner />
        </BrowserRouter>
        <Toaster position="top-center" />
        <LocaleToast />
      </AuthProvider>
    </div>
  );
}

export default App;
